import { DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { lastValueFrom, map } from 'rxjs';
import {
  DropdownFilter,
  FinancialControllerService,
  PageableRequest,
  PaymentControllerService,
  PaymentErrorDetail,
  PaymentResponseMessageDetail,
  PaymentResponseMessageType
} from 'src/app/admin-api';
import {
  PageContent,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-payment-response-message-type-detail',
  templateUrl: './payment-response-message-type-detail.component.html',
  styleUrl: './payment-response-message-type-detail.component.scss',
  providers: [DatePipe]
})
export class PaymentResponseMessageTypeDetailComponent implements OnInit {
  @ViewChild('codesTable') codesTable: TableComponent;
  @ViewChild('paymentsTable') paymentTable: TableComponent;
  // codesTotal = computed(() => this.codesTable()?.totalRecords);
  // paymentsTotal = computed(() => this.paymentTable()?.totalRecords);

  private financialService = inject(FinancialControllerService);
  public paymentService = inject(PaymentControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private title = inject(Title);
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);
  private datePipe = inject(DatePipe);

  permission = false;
  isMobile: boolean;
  paymentResponseMessageType: PaymentResponseMessageType | undefined;
  paymentResponseMessageTypeId: number | undefined;
  cols = [
    new TableColumn(
      'Id',
      'paymentResponseMessageId',
      true,
      'number',
      '/subscription-settings/payment-response-message/',
      'paymentResponseMessageId',
      true,
      'contains'
    ),
    new TableColumn(
      'Código',
      'code',
      true,
      'text',
      '/subscription-settings/payment-response-message/',
      'paymentResponseMessageId',
      true,
      'contains'
    ),
    new TableColumn(
      'Gateway',
      'gatewayName',
      true,
      'number',
      undefined,
      undefined,
      true,
      'in'
    )
  ];
  paymentCols = [
    new TableColumn.Builder()
      .setHeader('Id')
      .setField('creditCardPaymentId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('TID')
      .setField('TID')
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Gateway')
      .setField('gatewayName')
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Assinante')
      .setField('personName')
      .setCondition('contains')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('personId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id assinatura')
      .setField('subscriberId')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('subscriberId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('transactionStatusName')
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id do cartão')
      .setField('creditCardId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Código erro')
      .setField('lr')
      .setRouterLink('/subscription-settings/payment-response-message/')
      .setRouterLinkFieldName('paymentResponseMessageId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Info. Adicional')
      .setField('extraData')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo pagamento')
      .setField('creditCardPaymentType')
      .setCondition('contains')
      .build()
  ];
  selectedTab: number;
  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  };
  totalPayments: number;
  totalErrorCodes: number;

  async ngOnInit(): Promise<void> {
    this.checkScreenSize();
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue
    ]);
    this.activatedRoute.params.subscribe(async (params: Params) => {
      if (
        params['paymentResponseMessageTypeId'] &&
        params['paymentResponseMessageTypeId'] !== 'new' &&
        Number(params['paymentResponseMessageTypeId']) !==
          this.paymentResponseMessageTypeId
      ) {
        this.paymentResponseMessageTypeId = Number(
          params['paymentResponseMessageTypeId']
        );
        if (
          this.paymentResponseMessageTypeId !== undefined &&
          this.paymentResponseMessageTypeId !== null
        ) {
          LoaderService.showLoader();
          await this.findPaymentResponse(this.paymentResponseMessageTypeId);
        }
        const [gateways, statusList] = await Promise.all([
          this.getGateways(),
          this.getTransactionStatusList()
        ]);
        this.dropdownFilters = {
          gatewayName: gateways,
          transactionStatusName: statusList
        };
        this.title.setTitle(this.paymentResponseMessageType?.typeContent);
      } else if (!this.permission) {
        this.router.navigate(['/unauthorized']);
        LoaderService.showLoader(false);
      } else {
        AppDialogService.showErrorDialog(
          { message: 'Tipo de Erro não encontrado' },
          true
        );
        LoaderService.showLoader(false);
      }
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        if (queryParams['tab']) {
          this.selectedTab = Number(queryParams['tab']) || 0;
        } else {
          this.selectedTab = 0;
        }
      });
    });
  }

  async findPaymentResponse(
    paymentResponseMessageTypeId: number
  ): Promise<void> {
    try {
      this.paymentResponseMessageType = await lastValueFrom(
        this.financialService
          .getPaymentResponseMessageType(paymentResponseMessageTypeId)
          .pipe(map((data) => data.result))
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error, true);
    }
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  goToResponseMessageType(index?: number): void {
    this.selectedTab = index ?? this.selectedTab;
    this.router.navigate(
      [
        '/subscription-settings/payment-response-message-type/' +
          this.paymentResponseMessageType?.paymentResponseMessageTypeId
      ],
      {
        queryParams: { tab: this.selectedTab },
        queryParamsHandling: 'merge'
      }
    );
  }

  async getGateways() {
    return lastValueFrom(
      this.financialService.findGatewaysList().pipe(
        map((data) =>
          data.result.map(
            (g) =>
              ({
                label: g.gatewayId + '. ' + g.gatewayName,
                value: g.gatewayId.toString()
              }) as DropdownFilter
          )
        )
      )
    );
  }

  async getTransactionStatusList() {
    return lastValueFrom(
      this.financialService.findTransactionStatusList().pipe(
        map((data) =>
          data.result.map(
            (status) =>
              ({
                label: status.transactionStatusName,
                value: status.transactionStatusId.toString()
              }) as DropdownFilter
          )
        )
      )
    );
  }

  async getCreditCardPaymentTypes() {
    return lastValueFrom(
      this.paymentService
        .getAllCreditCardPaymentTypes()
        .pipe(map((data) => data.result))
    );
  }

  async findPaymentPage(
    request: PageableRequest,
    service: unknown
  ): Promise<PageContent<PaymentErrorDetail>> {
    const gatewayFilter = request.filters?.find(
      (f) => f.field === 'gatewayName'
    );
    if (
      gatewayFilter &&
      gatewayFilter.value &&
      Array.isArray(gatewayFilter.value) &&
      gatewayFilter.value.length > 0
    ) {
      request.filters.push({
        condition: 'in',
        field: 'gatewayId',
        value: gatewayFilter.value,
        fieldType: 'number'
      });
    }

    const transactionStatusFilter = request.filters?.find(
      (f) => f.field === 'transactionStatusName'
    );
    if (transactionStatusFilter && transactionStatusFilter.value) {
      request.filters.push({
        condition: 'in',
        field: 'transactionStatusId',
        value: transactionStatusFilter.value,
        fieldType: 'number'
      });
    }

    const creditCardPaymentTypeFilter = request.filters?.find(
      (f) => f.field === 'creditCardPaymentType'
    );
    if (creditCardPaymentTypeFilter && creditCardPaymentTypeFilter.value) {
      request.filters.push({
        condition: 'in',
        field: 'creditCardPaymentTypeId',
        value: creditCardPaymentTypeFilter.value,
        fieldType: 'number'
      });
    }

    return lastValueFrom(
      (service as PaymentControllerService)
        .findPaymentErrorDetailPage(request)
        .pipe(map((data) => data.result as PageContent<PaymentErrorDetail>))
    );
  }

  async findPage(
    request: PageableRequest,
    service: unknown
  ): Promise<PageContent<PaymentResponseMessageDetail>> {
    try {
      const gatewayFilter = request.filters?.find(
        (f) => f.field === 'gatewayName'
      );
      if (
        gatewayFilter &&
        gatewayFilter.value &&
        Array.isArray(gatewayFilter.value) &&
        gatewayFilter.value.length > 0
      ) {
        request.filters.push({
          condition: 'in',
          field: 'gatewayId',
          value: gatewayFilter.value,
          fieldType: 'number'
        });
      }
      const typeFilter = request.filters?.find(
        (f) => f.field === 'typeContent'
      );
      if (
        typeFilter &&
        typeFilter.value &&
        Array.isArray(typeFilter.value) &&
        typeFilter.value.length > 0
      ) {
        request.filters.push({
          condition: 'in',
          field: 'paymentResponseMessageTypeId',
          value: typeFilter.value,
          fieldType: 'number'
        });
      }

      return lastValueFrom(
        (service as FinancialControllerService)
          .listPaymentResponseMessagesDetailed(request)
          .pipe(
            map(
              (data) => data.result as PageContent<PaymentResponseMessageDetail>
            )
          )
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  setTotal(): void {
    if (this.codesTable?.pageContent && this.totalErrorCodes === undefined)
      this.totalErrorCodes = this.codesTable.pageContent?.totalElements;
    if (this.paymentTable?.pageContent && this.totalPayments === undefined)
      this.totalPayments = this.paymentTable.pageContent?.totalElements;
  }
}
